// Kho dữ liệu POS dùng chung giữa các màn (mock, lưu localStorage — sẽ thay bằng API khi có backend)
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  BRANCHES, CATALOG, DEFAULT_CONFIG, MANAGERS, METHODS, SEED_CUSTOMERS, STAFF, seedPromotions, seedVouchers,
  type AppointmentSnapshot, type CartLine, type Customer, type Draft, type DiscountType, type ImportBatch, type Manager, type Order,
  type OrderStatus, type PaymentLineInput, type PaymentMethod, type PosConfig, type PosRole, type Promotion,
  type Refund, type RefundLine, type ShiftClose, type Transaction, type Voucher,
} from './data';
import { calcOrder, type PricingResult } from './pricing';

export const POS_STORAGE_KEY = 'khothi-pos-v1';

const pad = (n: number, w: number) => String(n).padStart(w, '0');
const yymmdd = (d: Date) => `${pad(d.getFullYear() % 100, 2)}${pad(d.getMonth() + 1, 2)}${pad(d.getDate(), 2)}`;
const isSameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();

export interface CreateOrderInput {
  branchId: string;
  cashier: string;
  customer: Customer | null;
  pricing: PricingResult;
  orderDiscountInput: { type: DiscountType; value: number };
  voucherCode: string;
  approvedBy?: string;
  note: string;
  payments: PaymentLineInput[];
  draftId?: string | null;
  /** Đơn thu cho lịch hẹn khách đã xác nhận làm */
  appointment?: AppointmentSnapshot | null;
}

export interface CreateRefundInput {
  orderId: string;
  lines: RefundLine[];
  method: PaymentMethod;
  txnRef: string;
  reason: string;
  by: string;
  canApprove: boolean;
}

interface PosState {
  config: PosConfig;
  managers: Manager[];
  customers: Customer[];
  promotions: Promotion[];
  vouchers: Voucher[];
  orders: Order[];
  transactions: Transaction[];
  refunds: Refund[];
  drafts: Draft[];
  shiftCloses: ShiftClose[];
  importBatches: ImportBatch[];
  seq: { order: Record<string, number>; txn: number; refund: number; draft: number; batch: number };
  workingBranch: string;
  demoRole: PosRole | null;

  setWorkingBranch: (id: string) => void;
  setDemoRole: (role: PosRole | null) => void;
  addCustomer: (c: { name: string; phone: string; source?: string }) => Customer;
  peekOrderCode: (branchId: string) => string;
  createOrder: (input: CreateOrderInput) => { order: Order; change: number };
  collectDebt: (orderId: string, payments: PaymentLineInput[], cashier: string) => { change: number };
  cancelOrder: (orderId: string, reason: string, by: string) => void;
  markPrinted: (orderId: string, by: string, size: 'K80' | 'A4') => void;
  logOrder: (orderId: string, by: string, action: string) => void;
  createRefund: (input: CreateRefundInput) => Refund;
  approveRefund: (id: string, by: string) => void;
  rejectRefund: (id: string, by: string, reason: string) => void;
  payRefund: (id: string, by: string, txnRef: string) => void;
  saveDraft: (d: Omit<Draft, 'id' | 'createdAt' | 'updatedAt' | 'lockedBy' | 'lockedAt' | 'createdBy'> & { id?: string | null; by: string }) => Draft;
  deleteDrafts: (ids: string[]) => void;
  renameDraft: (id: string, name: string) => void;
  lockDraft: (id: string, by: string) => void;
  unlockDraft: (id: string) => void;
  purgeExpiredDrafts: () => number;
  upsertPromotion: (p: Promotion) => void;
  upsertVoucher: (v: Voucher, originalCode?: string) => void;
  setConfig: (patch: Partial<PosConfig>) => void;
  setManagerPin: (managerId: string, pin: string) => void;
  closeShift: (s: Omit<ShiftClose, 'id'>) => void;
  importOrders: (batch: Omit<ImportBatch, 'id' | 'status'>, orders: Order[]) => ImportBatch;
  rollbackImport: (batchId: string) => void;
  resetDemo: () => void;
}

// ---------------------------------------------------------------------------
// Sinh dữ liệu mẫu 45 ngày gần nhất (giả ngẫu nhiên, cố định theo seed)

const mulberry32 = (seed: number) => () => {
  seed |= 0; seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

type SeedData = Pick<PosState, 'customers' | 'promotions' | 'vouchers' | 'orders' | 'transactions' | 'refunds' | 'drafts' | 'seq'>;

function buildSeed(now = new Date()): SeedData {
  const rnd = mulberry32(20261008);
  const pick = <T,>(arr: T[]) => arr[Math.floor(rnd() * arr.length)];
  const promotions = seedPromotions(now);
  const vouchers = seedVouchers(now);
  const customers = SEED_CUSTOMERS.map(c => ({ ...c }));
  const orders: Order[] = [];
  const transactions: Transaction[] = [];
  const refunds: Refund[] = [];
  const seq: SeedData['seq'] = { order: {}, txn: 0, refund: 0, draft: 0, batch: 0 };

  const nextCode = (branchId: string, d: Date) => {
    const b = BRANCHES.find(x => x.id === branchId)!;
    const k = `${b.prefix}-${yymmdd(d)}`;
    seq.order[k] = (seq.order[k] || 0) + 1;
    return `POS-${k}-${pad(seq.order[k], 4)}`;
  };
  const nextTxn = () => `GD-${pad(++seq.txn, 6)}`;

  const makeLine = (itemId: string, qty: number, extra: Partial<CartLine> = {}): CartLine => {
    const it = CATALOG.find(c => c.id === itemId)!;
    return {
      key: `${itemId}-${Math.floor(rnd() * 1e9)}`, itemId, code: it.code, name: it.name, type: it.type,
      listPrice: it.price, qty, discountType: '%', discountValue: 0, staffId: '', note: '', ...extra,
    };
  };

  const pushOrder = (
    at: Date, branchId: string, customer: Customer | null, lines: CartLine[],
    opts: { orderDiscount?: { type: DiscountType; value: number }; split?: PaymentMethod[]; debt?: number; cashier?: string } = {},
  ) => {
    const pricing = calcOrder(lines, {
      branchId, promotions, orderDiscount: opts.orderDiscount || { type: '%', value: 0 }, voucher: null, roundingUnit: 1000, now: at,
    });
    const total = pricing.totals.total;
    const debt = Math.min(opts.debt || 0, total);
    const toPay = total - debt;
    const methods = opts.split || [pick<PaymentMethod>(['cash', 'transfer', 'transfer', 'card', 'ewallet'])];
    const cashier = opts.cashier || pick(STAFF.filter(s => s.branchId === branchId)).name;
    const id = nextCode(branchId, at);
    let left = toPay;
    methods.forEach((m, i) => {
      const amount = i === methods.length - 1 ? left : Math.round(toPay / methods.length / 1000) * 1000;
      left -= amount;
      if (amount <= 0) return;
      transactions.push({
        id: nextTxn(), orderId: id, branchId, at: at.toISOString(), kind: 'payment', method: m, amount,
        txnRef: m === 'cash' || m === 'prepaid' ? '' : `FT${yymmdd(at)}${pad(Math.floor(rnd() * 99999), 5)}`,
        cashier,
      });
    });
    const order: Order = {
      id, branchId, createdAt: at.toISOString(), cashier,
      customerId: customer?.id || null, customerName: customer?.name || 'Khách lẻ', customerPhone: customer?.phone || '',
      lines: pricing.lines, totals: pricing.totals, orderDiscountInput: opts.orderDiscount || { type: '%', value: 0 },
      voucherCode: '', note: '', paid: toPay, refunded: 0, debt, status: debt > 0 ? 'debt' : 'completed',
      source: 'pos', printCount: 1,
      logs: [{ at: at.toISOString(), by: cashier, action: `Tạo đơn và thu ${new Intl.NumberFormat('vi-VN').format(toPay)}đ` }],
    };
    orders.push(order);
    return order;
  };

  // Lịch sử 45 ngày
  for (let day = 45; day >= 1; day--) {
    for (const b of BRANCHES) {
      const count = Math.floor(rnd() * (b.id === 'q1' ? 5 : 3)) + 1;
      for (let k = 0; k < count; k++) {
        const at = new Date(now.getFullYear(), now.getMonth(), now.getDate() - day, 9 + Math.floor(rnd() * 10), Math.floor(rnd() * 60));
        const lines: CartLine[] = [];
        const n = 1 + Math.floor(rnd() * 3);
        for (let j = 0; j < n; j++) {
          const it = pick(CATALOG.filter(c => c.id !== 'k1' || rnd() < 0.2));
          if (lines.some(l => l.itemId === it.id)) continue;
          lines.push(makeLine(it.id, it.type === 'product' ? 1 + Math.floor(rnd() * 2) : 1, {
            staffId: pick(STAFF.filter(s => s.branchId === b.id)).id,
            ...(rnd() < 0.15 ? { discountType: '%' as const, discountValue: 5 } : {}),
          }));
        }
        const customer = rnd() < 0.75 ? pick(customers) : null;
        pushOrder(at, b.id, customer, lines, {
          split: rnd() < 0.15 ? ['cash', 'transfer'] : undefined,
          orderDiscount: rnd() < 0.1 ? { type: '%', value: 5 } : undefined,
        });
      }
    }
  }

  // Hôm nay: đơn ví dụ trong đặc tả (45.900.000 → 39.330.000)
  const today = (h: number, m: number) => new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m);
  const ngoc = customers[0];
  pushOrder(today(9, 12), 'q1', customers[2], [makeLine('s5', 1, { staffId: 'mai' })], { split: ['cash'], cashier: 'Lan' });
  const sample = pushOrder(today(10, 42), 'q1', ngoc, [
    makeLine('s1', 1, { staffId: 'lan' }),
    makeLine('p1', 2, { staffId: 'mai' }),
  ], { orderDiscount: { type: '%', value: 5 }, split: ['cash', 'transfer'], cashier: 'Lan' });
  pushOrder(today(11, 5), 'q1', customers[3], [makeLine('s2', 1, { staffId: 'ha' })], { debt: 3_000_000, split: ['card'], cashier: 'Mai' });
  pushOrder(today(11, 30), 'q3', customers[4], [makeLine('s4', 1, { staffId: 'minh' }), makeLine('p3', 1)], { split: ['ewallet'], cashier: 'Thảo' });
  pushOrder(today(12, 10), 'q10', null, [makeLine('s6', 1, { staffId: 'nhi' })], { split: ['cash'], cashier: 'Tuấn' });
  pushOrder(today(13, 20), 'q1', customers[6], [makeLine('k2', 1, { staffId: 'lan' })], { debt: 10_000_000, split: [], cashier: 'Lan' });

  // Đơn còn nợ mấy ngày trước
  const debtOld = orders.find(o => o.branchId === 'q3' && new Date(o.createdAt) < today(0, 0) && o.totals.total > 3_000_000 && o.customerId);
  if (debtOld) {
    const owed = Math.round(debtOld.totals.total * 0.4 / 1000) * 1000;
    debtOld.debt = owed;
    debtOld.paid -= owed;
    debtOld.status = 'debt';
    const lastTxn = [...transactions].reverse().find(t => t.orderId === debtOld.id);
    if (lastTxn) lastTxn.amount -= owed;
  }

  // Phiếu hoàn: 1 đã chi (hoàn 1 Serum), 1 chờ duyệt, 1 đã duyệt chờ chi
  const refundable = orders.filter(o => o.status === 'completed' && new Date(o.createdAt) < today(0, 0));
  const mkRefund = (order: Order, status: Refund['status'], lineIdx: number, qty: number, method: PaymentMethod, reason: string, at: Date) => {
    const line = order.lines[lineIdx];
    const amount = qty >= line.qty - line.refundedQty ? line.net - line.refundedAmount : Math.round(line.net / line.qty * qty);
    const r: Refund = {
      id: `HT-${pad(++seq.refund, 6)}`, orderId: order.id, branchId: order.branchId, createdAt: at.toISOString(),
      createdBy: order.cashier, lines: [{ lineKey: line.key, name: line.name, qty, amount }], amount, method, txnRef: '',
      reason, status,
      ...(status !== 'pending' ? { approvedBy: 'Quản lý Hà (Q1)', approvedAt: at.toISOString() } : {}),
    };
    if (status === 'paid') {
      r.paidAt = at.toISOString();
      line.refundedQty += qty;
      line.refundedAmount += amount;
      order.refunded += amount;
      order.status = order.lines.every(l => l.refundedQty >= l.qty) ? 'refunded' : 'partially_refunded';
      order.logs.push({ at: at.toISOString(), by: r.createdBy, action: `Hoàn ${new Intl.NumberFormat('vi-VN').format(amount)}đ (${r.id})` });
      transactions.push({ id: nextTxn(), orderId: order.id, branchId: order.branchId, at: at.toISOString(), kind: 'refund', method, amount: -amount, txnRef: '', cashier: r.createdBy, refundId: r.id });
    } else {
      order.logs.push({ at: at.toISOString(), by: r.createdBy, action: `Tạo phiếu hoàn ${r.id} – chờ duyệt` });
    }
    refunds.push(r);
  };
  const smallOrder = refundable.find(o => o.lines.length >= 1 && o.totals.total < 2_000_000);
  if (smallOrder) mkRefund(smallOrder, 'paid', 0, 1, 'cash', 'Khách dị ứng sản phẩm, hoàn tiền theo chính sách', new Date(smallOrder.createdAt));
  const bigOrder = refundable.reverse().find(o => o.totals.total >= 3_000_000 && o.lines.length === 1);
  if (bigOrder) mkRefund(bigOrder, 'pending', 0, 1, 'transfer', 'Khách không thực hiện được dịch vụ do sức khỏe', today(8, 50));
  const midOrder = refundable.find(o => o !== bigOrder && o.totals.total >= 2_000_000 && o.lines.length === 1);
  if (midOrder) mkRefund(midOrder, 'approved', 0, 1, 'transfer', 'Đặt nhầm dịch vụ, khách đổi sang gói khác', today(9, 40));

  // Nháp
  const draftLines: CartLine[] = [makeLine('s1', 1, { staffId: 'lan', discountValue: 0 }), makeLine('p1', 1)];
  const hifuOldPrice = { ...makeLine('s2', 1), listPrice: 14_000_000 };
  const drafts: Draft[] = [
    {
      id: `NH-${pad(++seq.draft, 5)}`, name: 'Khách - 10:30', branchId: 'q1', createdBy: 'Lan', createdAt: today(10, 30).toISOString(), updatedAt: today(10, 30).toISOString(),
      customerId: null, lines: draftLines, orderDiscount: { type: '%', value: 0 }, voucherCode: '', note: '', total: 40_950_000, lockedBy: null, lockedAt: null,
    },
    {
      id: `NH-${pad(++seq.draft, 5)}`, name: 'Chị Mai Thermage', branchId: 'q1', createdBy: 'Hoa', createdAt: today(9, 15).toISOString(), updatedAt: today(9, 15).toISOString(),
      customerId: 'c2', lines: [makeLine('s1', 1)], orderDiscount: { type: '%', value: 0 }, voucherCode: '', note: 'Khách hẹn quay lại sau 14h', total: 40_500_000,
      lockedBy: 'Hoa', lockedAt: new Date(now.getTime() - 30 * 60000).toISOString(),
    },
    {
      id: `NH-${pad(++seq.draft, 5)}`, name: 'Khách lẻ Hifu', branchId: 'q1', createdBy: 'Mai',
      createdAt: new Date(now.getTime() - 6.6 * 86400000).toISOString(), updatedAt: new Date(now.getTime() - 6.6 * 86400000).toISOString(),
      customerId: null, lines: [hifuOldPrice, makeLine('p3', 2)], orderDiscount: { type: '%', value: 0 }, voucherCode: '', note: '', total: 14_640_000, lockedBy: null, lockedAt: null,
    },
  ];

  void sample;
  return { customers, promotions, vouchers, orders: orders.reverse(), transactions: transactions.reverse(), refunds: refunds.reverse(), drafts, seq };
}

// ---------------------------------------------------------------------------

const applyRefundToOrder = (order: Order, refund: Refund, at: string, by: string): Order => {
  const lines = order.lines.map(l => {
    const rl = refund.lines.find(x => x.lineKey === l.key);
    return rl ? { ...l, refundedQty: l.refundedQty + rl.qty, refundedAmount: l.refundedAmount + rl.amount } : l;
  });
  const status: OrderStatus = lines.every(l => l.refundedQty >= l.qty) ? 'refunded' : 'partially_refunded';
  return {
    ...order,
    lines,
    refunded: order.refunded + refund.amount,
    status,
    logs: [...order.logs, { at, by, action: `Chi hoàn ${new Intl.NumberFormat('vi-VN').format(refund.amount)}đ (${refund.id})` }],
  };
};

const usePosStore = create<PosState>()(
  persist(
    (set, get) => ({
      config: DEFAULT_CONFIG,
      managers: MANAGERS,
      ...buildSeed(),
      shiftCloses: [],
      importBatches: [],
      workingBranch: 'q1',
      demoRole: null,

      setWorkingBranch: id => set({ workingBranch: id }),
      setDemoRole: role => set({ demoRole: role }),

      addCustomer: ({ name, phone, source }) => {
        const c: Customer = { id: `c${Date.now()}`, name: name.trim(), phone, tier: 'Thường', prepaid: 0, source };
        set(s => ({ customers: [c, ...s.customers] }));
        return c;
      },

      peekOrderCode: branchId => {
        const b = BRANCHES.find(x => x.id === branchId)!;
        const k = `${b.prefix}-${yymmdd(new Date())}`;
        return `POS-${k}-${pad((get().seq.order[k] || 0) + 1, 4)}`;
      },

      createOrder: input => {
        const s = get();
        const now = new Date();
        const at = now.toISOString();
        const b = BRANCHES.find(x => x.id === input.branchId)!;
        const k = `${b.prefix}-${yymmdd(now)}`;
        const n = (s.seq.order[k] || 0) + 1;
        const id = `POS-${k}-${pad(n, 4)}`;
        const total = input.pricing.totals.total;

        // Tiền mặt được nhập vượt — phần vượt là tiền thối, không ghi vào doanh thu (BR2.1)
        const entered = input.payments.reduce((sum, p) => sum + p.amount, 0);
        let excess = Math.max(0, entered - total);
        let change = 0;
        let txnSeq = s.seq.txn;
        const txns: Transaction[] = [];
        input.payments.forEach(p => {
          let amount = p.amount;
          if (p.method === 'cash' && excess > 0) {
            const cut = Math.min(excess, amount);
            amount -= cut;
            excess -= cut;
          }
          const lineChange = p.method === 'cash' ? Math.max(0, p.tendered - amount) : 0;
          change += lineChange;
          if (amount <= 0) return;
          txns.push({
            id: `GD-${pad(++txnSeq, 6)}`, orderId: id, branchId: input.branchId, at, kind: 'payment', method: p.method, amount,
            txnRef: p.txnRef.trim().toUpperCase(), cashier: input.cashier,
            ...(p.method === 'cash' ? { tendered: p.tendered, change: lineChange } : {}),
          });
        });
        const paid = txns.reduce((sum, t) => sum + t.amount, 0);
        const debt = Math.max(0, total - paid);
        const fmt = (x: number) => new Intl.NumberFormat('vi-VN').format(x);

        const logs = [{ at, by: input.cashier, action: 'Tạo đơn tại quầy' }];
        if (input.appointment) logs.push({ at, by: input.cashier, action: `Thu tiền lịch hẹn ${input.appointment.code}${input.appointment.consultant ? ` (tư vấn: ${input.appointment.consultant})` : ''}` });
        if (input.pricing.totals.itemPromo || input.pricing.totals.orderPromo) logs.push({ at, by: 'Hệ thống', action: `Áp khuyến mãi tự động -${fmt(input.pricing.totals.itemPromo + input.pricing.totals.orderPromo)}đ` });
        if (input.approvedBy) logs.push({ at, by: input.approvedBy, action: `Duyệt chiết khấu ${input.pricing.manualPercent.toFixed(1)}%` });
        if (input.voucherCode) logs.push({ at, by: input.cashier, action: `Áp voucher ${input.voucherCode} -${fmt(input.pricing.totals.voucher)}đ` });
        txns.forEach(t => logs.push({ at, by: input.cashier, action: `Thu ${fmt(t.amount)}đ – ${t.method === 'cash' ? 'Tiền mặt' : t.txnRef || t.method}` }));
        if (debt > 0) logs.push({ at, by: input.cashier, action: `Ghi nợ ${fmt(debt)}đ` });

        const order: Order = {
          id, branchId: input.branchId, createdAt: at, cashier: input.cashier,
          customerId: input.customer?.id || null, customerName: input.customer?.name || 'Khách lẻ', customerPhone: input.customer?.phone || '',
          lines: input.pricing.lines, totals: input.pricing.totals, orderDiscountInput: input.orderDiscountInput,
          voucherCode: input.voucherCode, approvedBy: input.approvedBy, note: input.note,
          paid, refunded: 0, debt, status: debt > 0 ? 'debt' : 'completed', source: 'pos', printCount: 0, logs,
          ...(input.appointment ? { appointment: input.appointment } : {}),
        };

        const prepaidUsed = input.payments.filter(p => p.method === 'prepaid').reduce((sum, p) => sum + p.amount, 0);
        set(st => ({
          orders: [order, ...st.orders],
          transactions: [...txns.reverse(), ...st.transactions],
          seq: { ...st.seq, order: { ...st.seq.order, [k]: n }, txn: txnSeq },
          drafts: input.draftId ? st.drafts.filter(d => d.id !== input.draftId) : st.drafts,
          vouchers: input.voucherCode ? st.vouchers.map(v => (v.code === input.voucherCode ? { ...v, used: v.used + 1 } : v)) : st.vouchers,
          customers: prepaidUsed && input.customer
            ? st.customers.map(c => (c.id === input.customer!.id ? { ...c, prepaid: c.prepaid - prepaidUsed } : c))
            : st.customers,
        }));
        return { order, change };
      },

      collectDebt: (orderId, payments, cashier) => {
        const s = get();
        const order = s.orders.find(o => o.id === orderId);
        if (!order) return { change: 0 };
        const at = new Date().toISOString();
        let excess = Math.max(0, payments.reduce((sum, p) => sum + p.amount, 0) - order.debt);
        let change = 0;
        let txnSeq = s.seq.txn;
        const txns: Transaction[] = [];
        payments.forEach(p => {
          let amount = p.amount;
          if (p.method === 'cash' && excess > 0) { const cut = Math.min(excess, amount); amount -= cut; excess -= cut; }
          const lineChange = p.method === 'cash' ? Math.max(0, p.tendered - amount) : 0;
          change += lineChange;
          if (amount <= 0) return;
          txns.push({
            id: `GD-${pad(++txnSeq, 6)}`, orderId, branchId: order.branchId, at, kind: 'debt_collection', method: p.method, amount,
            txnRef: p.txnRef.trim().toUpperCase(), cashier, ...(p.method === 'cash' ? { tendered: p.tendered, change: lineChange } : {}),
          });
        });
        const got = txns.reduce((sum, t) => sum + t.amount, 0);
        const debt = Math.max(0, order.debt - got);
        const fmt = (x: number) => new Intl.NumberFormat('vi-VN').format(x);
        const prepaidUsed = payments.filter(p => p.method === 'prepaid').reduce((sum, p) => sum + p.amount, 0);
        set(st => ({
          orders: st.orders.map(o => (o.id === orderId ? {
            ...o, paid: o.paid + got, debt, status: debt > 0 ? 'debt' : (o.refunded > 0 ? o.status : 'completed'),
            logs: [...o.logs, { at, by: cashier, action: `Thu nợ ${fmt(got)}đ${debt > 0 ? `, còn nợ ${fmt(debt)}đ` : ' – đã thu đủ'}` }],
          } : o)),
          transactions: [...txns.reverse(), ...st.transactions],
          seq: { ...st.seq, txn: txnSeq },
          customers: prepaidUsed && order.customerId
            ? st.customers.map(c => (c.id === order.customerId ? { ...c, prepaid: c.prepaid - prepaidUsed } : c))
            : st.customers,
        }));
        return { change };
      },

      cancelOrder: (orderId, reason, by) => {
        const at = new Date().toISOString();
        set(st => ({
          orders: st.orders.map(o => (o.id === orderId ? { ...o, status: 'cancelled', debt: 0, cancelReason: reason, logs: [...o.logs, { at, by, action: `Hủy đơn: ${reason}` }] } : o)),
        }));
      },

      markPrinted: (orderId, by, size) => {
        const at = new Date().toISOString();
        set(st => ({
          orders: st.orders.map(o => (o.id === orderId ? {
            ...o, printCount: o.printCount + 1,
            logs: [...o.logs, { at, by, action: `${o.printCount > 0 ? 'In lại' : 'In'} hóa đơn ${size}` }],
          } : o)),
        }));
      },

      logOrder: (orderId, by, action) => {
        const at = new Date().toISOString();
        set(st => ({ orders: st.orders.map(o => (o.id === orderId ? { ...o, logs: [...o.logs, { at, by, action }] } : o)) }));
      },

      createRefund: input => {
        const s = get();
        const order = s.orders.find(o => o.id === input.orderId)!;
        const now = new Date();
        const at = now.toISOString();
        const amount = input.lines.reduce((sum, l) => sum + l.amount, 0);
        // R1: cần duyệt khi ≥ ngưỡng hoặc đơn không thuộc hôm nay; người có pos.approve được bỏ qua
        const needApproval = !input.canApprove && (amount >= s.config.refundApprovalThreshold || !isSameDay(new Date(order.createdAt), now));
        const refund: Refund = {
          id: `HT-${pad(s.seq.refund + 1, 6)}`, orderId: order.id, branchId: order.branchId, createdAt: at, createdBy: input.by,
          lines: input.lines, amount, method: input.method, txnRef: input.txnRef.trim().toUpperCase(), reason: input.reason.trim(),
          status: needApproval ? 'pending' : 'paid',
          ...(needApproval ? {} : { approvedBy: input.by, approvedAt: at, paidAt: at }),
        };
        const fmt = (x: number) => new Intl.NumberFormat('vi-VN').format(x);
        set(st => {
          let orders = st.orders;
          let transactions = st.transactions;
          let txn = st.seq.txn;
          let customers = st.customers;
          if (needApproval) {
            orders = orders.map(o => (o.id === order.id ? { ...o, logs: [...o.logs, { at, by: input.by, action: `Tạo phiếu hoàn ${refund.id} ${fmt(amount)}đ – chờ duyệt` }] } : o));
          } else {
            orders = orders.map(o => (o.id === order.id ? applyRefundToOrder(o, refund, at, input.by) : o));
            transactions = [{ id: `GD-${pad(++txn, 6)}`, orderId: order.id, branchId: order.branchId, at, kind: 'refund', method: input.method, amount: -amount, txnRef: refund.txnRef, cashier: input.by, refundId: refund.id }, ...transactions];
            if (input.method === 'prepaid' && order.customerId) customers = customers.map(c => (c.id === order.customerId ? { ...c, prepaid: c.prepaid + amount } : c));
          }
          return { refunds: [refund, ...st.refunds], orders, transactions, customers, seq: { ...st.seq, refund: st.seq.refund + 1, txn } };
        });
        return refund;
      },

      approveRefund: (id, by) => {
        const at = new Date().toISOString();
        set(st => {
          const r = st.refunds.find(x => x.id === id)!;
          return {
            refunds: st.refunds.map(x => (x.id === id ? { ...x, status: 'approved', approvedBy: by, approvedAt: at } : x)),
            orders: st.orders.map(o => (o.id === r.orderId ? { ...o, logs: [...o.logs, { at, by, action: `Duyệt phiếu hoàn ${id}` }] } : o)),
          };
        });
      },

      rejectRefund: (id, by, reason) => {
        const at = new Date().toISOString();
        set(st => {
          const r = st.refunds.find(x => x.id === id)!;
          return {
            refunds: st.refunds.map(x => (x.id === id ? { ...x, status: 'rejected', approvedBy: by, approvedAt: at, rejectReason: reason } : x)),
            orders: st.orders.map(o => (o.id === r.orderId ? { ...o, logs: [...o.logs, { at, by, action: `Từ chối phiếu hoàn ${id}: ${reason}` }] } : o)),
          };
        });
      },

      payRefund: (id, by, txnRef) => {
        const at = new Date().toISOString();
        set(st => {
          const r = { ...st.refunds.find(x => x.id === id)!, status: 'paid' as const, paidAt: at, txnRef: txnRef.trim().toUpperCase() || st.refunds.find(x => x.id === id)!.txnRef };
          const order = st.orders.find(o => o.id === r.orderId)!;
          const txn = st.seq.txn + 1;
          return {
            refunds: st.refunds.map(x => (x.id === id ? r : x)),
            orders: st.orders.map(o => (o.id === r.orderId ? applyRefundToOrder(o, r, at, by) : o)),
            transactions: [{ id: `GD-${pad(txn, 6)}`, orderId: r.orderId, branchId: r.branchId, at, kind: 'refund', method: r.method, amount: -r.amount, txnRef: r.txnRef, cashier: by, refundId: r.id }, ...st.transactions],
            customers: r.method === 'prepaid' && order.customerId ? st.customers.map(c => (c.id === order.customerId ? { ...c, prepaid: c.prepaid + r.amount } : c)) : st.customers,
            seq: { ...st.seq, txn },
          };
        });
      },

      saveDraft: d => {
        const s = get();
        const at = new Date().toISOString();
        const existing = d.id ? s.drafts.find(x => x.id === d.id) : undefined;
        const draft: Draft = existing
          ? { ...existing, ...d, id: existing.id, updatedAt: at, lockedBy: null, lockedAt: null }
          : { ...d, id: `NH-${pad(s.seq.draft + 1, 5)}`, createdBy: d.by, createdAt: at, updatedAt: at, lockedBy: null, lockedAt: null };
        delete (draft as any).by;
        set(st => ({
          drafts: existing ? st.drafts.map(x => (x.id === draft.id ? draft : x)) : [draft, ...st.drafts],
          seq: existing ? st.seq : { ...st.seq, draft: st.seq.draft + 1 },
        }));
        return draft;
      },
      deleteDrafts: ids => set(st => ({ drafts: st.drafts.filter(d => !ids.includes(d.id)) })),
      renameDraft: (id, name) => set(st => ({ drafts: st.drafts.map(d => (d.id === id ? { ...d, name } : d)) })),
      lockDraft: (id, by) => set(st => ({ drafts: st.drafts.map(d => (d.id === id ? { ...d, lockedBy: by, lockedAt: new Date().toISOString() } : d)) })),
      unlockDraft: id => set(st => ({ drafts: st.drafts.map(d => (d.id === id ? { ...d, lockedBy: null, lockedAt: null } : d)) })),
      purgeExpiredDrafts: () => {
        const ttl = get().config.draftTtlDays * 86400000;
        const now = Date.now();
        const keep = get().drafts.filter(d => now - new Date(d.updatedAt).getTime() < ttl);
        const removed = get().drafts.length - keep.length;
        if (removed) set({ drafts: keep });
        return removed;
      },

      upsertPromotion: p => set(st => ({
        promotions: st.promotions.some(x => x.id === p.id) ? st.promotions.map(x => (x.id === p.id ? p : x)) : [p, ...st.promotions],
      })),
      upsertVoucher: (v, originalCode) => set(st => {
        const key = originalCode || v.code;
        return { vouchers: st.vouchers.some(x => x.code === key) ? st.vouchers.map(x => (x.code === key ? v : x)) : [v, ...st.vouchers] };
      }),
      setConfig: patch => set(st => ({ config: { ...st.config, ...patch } })),
      setManagerPin: (managerId, pin) => set(st => ({ managers: st.managers.map(m => (m.id === managerId ? { ...m, pin } : m)) })),

      closeShift: sc => set(st => ({ shiftCloses: [{ ...sc, id: `CA-${pad(st.shiftCloses.length + 1, 4)}` }, ...st.shiftCloses] })),

      importOrders: (batch, orders) => {
        const s = get();
        const id = `IMP-${pad(s.seq.batch + 1, 4)}`;
        const full: ImportBatch = { ...batch, id, status: 'done' };
        const withBatch = orders.map(o => ({ ...o, importBatchId: id }));
        const replacedIds = new Set(withBatch.map(o => o.id));
        set(st => ({
          orders: [...withBatch, ...(batch.mode === 'overwrite' ? st.orders.filter(o => !replacedIds.has(o.id)) : st.orders)],
          importBatches: [full, ...st.importBatches],
          seq: { ...st.seq, batch: st.seq.batch + 1 },
        }));
        return full;
      },
      rollbackImport: batchId => set(st => ({
        orders: st.orders.filter(o => o.importBatchId !== batchId),
        importBatches: st.importBatches.map(b => (b.id === batchId ? { ...b, status: 'rolled_back' } : b)),
      })),

      resetDemo: () => set({ ...buildSeed(), config: DEFAULT_CONFIG, managers: MANAGERS, shiftCloses: [], importBatches: [] }),
    }),
    { name: POS_STORAGE_KEY, version: 1 },
  ),
);

// Đồng bộ giữa các tab trình duyệt (mô phỏng realtime khi chưa có backend)
if (typeof window !== 'undefined') {
  window.addEventListener('storage', e => {
    if (e.key === POS_STORAGE_KEY) usePosStore.persist.rehydrate();
  });
}

export const emptyByMethod = () => METHODS.reduce((acc, m) => ({ ...acc, [m]: 0 }), {} as Record<PaymentMethod, number>);

export default usePosStore;
